import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'quartz-routing/react-router';
import { GET_SEARCH_FILTER_CONFIG_QUERY as PUBLIC_GET_SEARCH_FILTER_CONFIG_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/storefront/service/rpc/MarketplaceStorefrontPublicRpc.dfc.new';
import {
  FilterDataType,
  FilterOption,
  FilterOptionGroup,
} from '../../../__generated__/chirp/com/hubspot/marketplace/storefront/service/rpc/MarketplaceStorefrontRpc';
import { GET_SEARCH_FILTER_CONFIG_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/storefront/service/rpc/MarketplaceStorefrontRpc.dfc.new';
import { Paths } from '../../constants/paths';
import { useAppContextQuery } from '../../hooks/useAppContextQuery';
import { useProductTypes } from '../../hooks/useProductTypeSearchParams';
import { ProductType } from '../../utils/productType';

export type Filter = Readonly<{
  field: string;
  dataType: FilterDataType;
  hasMoreOptions: boolean;
  isMulti: boolean;
  label?: string;
  placeholder?: string;
  productType?: string;
  defaultValue?: string;
  options: ReadonlyArray<FilterOption>;
  optionGroups: ReadonlyArray<FilterOptionGroup>;
}>;

export type FilterConfigResult = Record<string, Filter | undefined> & {
  error?: Error;
  loading: boolean;
};

export function useFilterConfigData({
  allProductTypes,
}: { allProductTypes?: boolean } = {}) {
  const productTypes = useProductTypes();
  const includesWorkflowTemplates = productTypes.includes('WORKFLOW_TEMPLATE');
  return useAppContextQuery({
    app: GET_SEARCH_FILTER_CONFIG_QUERY,
    public: PUBLIC_GET_SEARCH_FILTER_CONFIG_QUERY,
    options: {
      variables: {
        language: getLang(),
        includeWorkflowTemplates: includesWorkflowTemplates,
        ...(!allProductTypes &&
          productTypes.length > 0 && {
            productTypes,
          }),
      },
    },
  });
}

/**
 * Important: Unlike `useFilterConfigData`, `useFilterConfig` will **NOT**
   return filters that are not applicable to the current situation.

   For example: product-specific filters will not be returned if they don't
   match the current product type.
 */
export function useFilterConfig(options?: {
  productTypes?: ProductType[];
}): FilterConfigResult {
  const params = useSearchParams();
  const rawProductTypeParam = params.get('PRODUCT_TYPE');
  const urlProductTypes = useProductTypes();
  const productTypes = (options?.productTypes ??
    urlProductTypes) as ProductType[];
  const isCollection = !!useParams<{ collectionSlug: string }>().collectionSlug;
  const location = useLocation();
  const { data, loading, error } = useFilterConfigData();

  const config = useMemo(() => {
    if (!data?.result?.filterConfigs) return {};

    const completeConfig: Record<string, Filter> = {};

    Object.values(data.result.filterConfigs).forEach((filter) => {
      let filterProductTypes = filter.productTypes as ProductType[];

      if (filter.field === 'PRODUCT_TYPE') {
        filterProductTypes = [...filterProductTypes, 'WORKFLOW_TEMPLATE'];
      }

      // ensure overlap between the filter's product types and the applicable product types
      if (!filterProductTypes.some((type) => productTypes.includes(type))) {
        return;
      }

      // On the "All" search tab, hide all filters
      if (location.pathname === Paths.SEARCH_RESULTS && !rawProductTypeParam) {
        return;
      }

      // If this is a collection, hide the product type filter
      if (filter.field === 'PRODUCT_TYPE' && isCollection) return;

      if (
        filter.field === 'PRODUCT_TYPE' &&
        location.pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS
      ) {
        return;
      }

      let filterOptions: ReadonlyArray<FilterOption> = filter.options ?? [];

      if (filter.field === 'PRODUCT_TYPE') {
        const hasWorkflowTemplate = filterOptions.some(
          (o) => o.value === 'WORKFLOW_TEMPLATE'
        );
        if (!hasWorkflowTemplate) {
          filterOptions = [
            ...filterOptions,
            {
              text: unescapedText(
                'marketplaceStorefront.filters.productType.workflowTemplates'
              ),
              value: 'WORKFLOW_TEMPLATE',
            },
          ];
        }
      }

      completeConfig[filter.field as string] = {
        field: filter.field as string,
        dataType: filter.dataType as Filter['dataType'],
        hasMoreOptions: !!filter.hasMoreOptions,
        isMulti: filter.isMulti,
        label: filter.label,
        placeholder: filter.placeholder ?? undefined,
        defaultValue: filter.defaultValue ?? undefined,
        productType:
          filter.productTypes.length === 1
            ? (filter.productTypes[0] as Filter['productType'])
            : undefined,
        options: filterOptions,
        optionGroups: filter.optionGroups ?? [],
      };
    });

    return completeConfig;
  }, [
    data,
    isCollection,
    location.pathname,
    productTypes,
    rawProductTypeParam,
  ]);

  return Object.assign(config, { error, loading });
}

export function useRemoveInapplicableFilters(): void {
  const filterConfigDataResult = useFilterConfigData({ allProductTypes: true });
  const rawFilters = filterConfigDataResult.data?.result.filterConfigs;
  const filterConfig = useFilterConfig();
  const params = useSearchParams();

  useEffect(() => {
    if (!rawFilters || filterConfig.loading || filterConfig.error) return;

    const allFilterFields = Object.keys(rawFilters);
    let changeMade = false;

    allFilterFields.forEach((filterField) => {
      const filterApplied = params.get(filterField);
      const filterApplicable = filterField in filterConfig;

      if (filterApplied && !filterApplicable) {
        params.delete(filterField, {
          historyMethod: 'replace',
          defer: true,
        });
        changeMade = true;
      }
    });

    if (changeMade) params.updateHistory();
  }, [rawFilters, filterConfig, params]);
}
